/**
 * Force configuration. Pure constants and one configure function, shared by
 * the worker and the main-thread engine so the two can never drift.
 */
import {
  forceLink,
  forceManyBody,
  forceX,
  forceY,
  forceCollide,
  type Simulation,
  type ForceLink,
  type ForceManyBody,
  type ForceX,
  type ForceY,
  type ForceCollide,
} from "d3-force";
import { nodeRadius } from "../radius";

export const ALPHA_MIN = 0.0012;
export const SETTLE_TICKS = 300;
/** Coupled to ALPHA_MIN so changing one cannot silently change the feel. */
export const ALPHA_DECAY = 1 - Math.pow(ALPHA_MIN, 1 / SETTLE_TICKS);
/** d3's default. Higher is more viscous. */
export const VELOCITY_DECAY = 0.4;

/** Obsidian's centre slider 0..1 maps onto this much forceX/Y strength. */
export const CENTER_GAIN = 0.09;
/** Obsidian's repel slider 0..20 maps onto this much negative charge. */
export const REPEL_GAIN = 22;
/** distanceMax = REPEL_RANGE * linkDistance. See below: this is fidelity. */
export const REPEL_RANGE = 4;
export const COLLIDE_PAD = 2;
/** Collision builds a second quadtree per tick, so it is the first to go. */
export const COLLIDE_MAX_NODES = 2500;
/** Above this, trade Barnes-Hut accuracy for speed. Visually undetectable. */
export const THETA_BUMP_NODES = 1200;

/** Warm target while dragging or growing, so neighbours react immediately. */
export const WARM_ALPHA = 0.3;
/** Gentle permanent warmth during creation playback: growth looks organic. */
export const PLAYBACK_ALPHA = 0.15;
/** Unpublished ticks on first mount, so the graph appears already laid out. */
export const PREWARM_TICKS = 120;

export interface SimNode {
  index?: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  fx?: number | null;
  fy?: number | null;
  /** Degree in the filtered graph; drives the collision radius. */
  deg: number;
}

export interface SimLink {
  source: SimNode | number;
  target: SimNode | number;
  index?: number;
}

export interface ForceOpts {
  centerStrength: number;
  repelStrength: number;
  linkStrength: number;
  linkDistance: number;
  nodeSizeMultiplier: number;
  nodeCount: number;
}

export interface ForceBundle {
  link: ForceLink<SimNode, SimLink>;
  charge: ForceManyBody<SimNode>;
  x: ForceX<SimNode>;
  y: ForceY<SimNode>;
  collide: ForceCollide<SimNode>;
}

export function createForces(): ForceBundle {
  return {
    link: forceLink<SimNode, SimLink>([]).id((_d, i) => i),
    charge: forceManyBody<SimNode>(),
    // NOT forceCenter: that recomputes the centroid every tick and rigidly
    // translates every node onto the target, so it cannot express weak
    // centring. forceX/Y are true positional springs, which is what
    // Obsidian's centre slider actually feels like.
    x: forceX<SimNode>(0),
    y: forceY<SimNode>(0),
    collide: forceCollide<SimNode>(),
  };
}

/**
 * Apply the four Obsidian sliders.
 *
 * Link strength SCALES d3's degree-normalized default rather than replacing
 * it. A flat value gives a degree-50 hub fifty times the pull of a leaf, the
 * layout goes rigid, and hubs are torn apart by their own edges. At slider 1
 * the config is identical to d3's default, which is why 1 is the default.
 *
 * distanceMax on the charge is a fidelity knob, not just a perf knob: without
 * it every node feels the whole cloud, so the graph's physical size grows
 * superlinearly with N and a large vault inflates into a diffuse haze.
 */
export function configureForces(f: ForceBundle, o: ForceOpts): void {
  const linkStrengthOf = (l: SimLink): number => {
    const s = typeof l.source === "object" ? l.source.deg : 1;
    const t = typeof l.target === "object" ? l.target.deg : 1;
    return o.linkStrength / Math.max(1, Math.min(s || 1, t || 1));
  };

  f.link.distance(o.linkDistance).strength(linkStrengthOf);

  f.charge
    .strength(-o.repelStrength * REPEL_GAIN)
    .distanceMin(1)
    .distanceMax(o.linkDistance * REPEL_RANGE)
    .theta(o.nodeCount > THETA_BUMP_NODES ? 1.15 : 0.9);

  const center = o.centerStrength * CENTER_GAIN;
  f.x.strength(center);
  f.y.strength(center);

  if (o.nodeCount > COLLIDE_MAX_NODES) {
    f.collide.radius(0).strength(0);
  } else {
    f.collide
      .radius((d) => nodeRadius(d.deg, o.nodeSizeMultiplier) + COLLIDE_PAD)
      .strength(0.7)
      .iterations(1);
  }
}

export function attachForces(sim: Simulation<SimNode, SimLink>, f: ForceBundle): void {
  sim
    .force("link", f.link)
    .force("charge", f.charge)
    .force("x", f.x)
    .force("y", f.y)
    .force("collide", f.collide)
    .alphaMin(ALPHA_MIN)
    .alphaDecay(ALPHA_DECAY)
    .velocityDecay(VELOCITY_DECAY);
}
